import React, { useMemo } from 'react';
import './CartReviewModal.css';

const CartReviewModal = ({
  isOpen,
  onClose,
  cartItems,
  userSettings,
  onSave,
  isDarkTheme = false,
  isSmokeandMirrors = false
}) => {
  // Currency symbols for formatting prices
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const currencySymbol = currencySymbols[userSettings?.currency || 'USD'] || '$';

  // Consolidation logic - group items by same SKU attributes AND same price
  const consolidatedItems = useMemo(() => {
    const groups = {};

    cartItems.forEach(item => {
      // Get the price based on payment type
      const price = item.isCash ? parseFloat(item.cashPrice) : parseFloat(item.creditPrice);

      // For video games, use variant instead of condition/printing
      const condition = item.isVideoGame ? (item.variant?.toUpperCase() || 'CIB') : item.condition;
      const printing = item.isVideoGame ? (item.consoleName || 'Video Game') : item.printing;

      // Key includes price to ensure same-priced items are grouped
      const key = `${item.productId || item.name}-${condition}-${printing}-${item.language || 'EN'}-${item.isCash}-${price.toFixed(2)}`;

      if (!groups[key]) {
        groups[key] = {
          ...item,
          displayCondition: condition,
          displayPrinting: printing,
          quantity: 1,
          unitPrice: price,
          lineTotal: price
        };
      } else {
        groups[key].quantity += 1;
        groups[key].lineTotal = groups[key].quantity * groups[key].unitPrice;
      }
    });

    return Object.values(groups);
  }, [cartItems]);

  // Calculate totals
  const totals = useMemo(() => {
    return consolidatedItems.reduce((acc, item) => {
      if (item.isCash) {
        acc.cash += item.lineTotal;
      } else {
        acc.credit += item.lineTotal;
      }
      acc.total += item.lineTotal;
      return acc;
    }, { cash: 0, credit: 0, total: 0 });
  }, [consolidatedItems]);

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Handle save - calls the existing save popup flow
  const handleSave = () => {
    if (onSave && typeof onSave === 'function') {
      onSave();
    }
  };

  if (!isOpen) return null;

  // Build modal class based on theme
  let modalClass = 'cart-review-modal';
  if (isDarkTheme) {
    modalClass += ' dark-theme';
  }
  if (isSmokeandMirrors) {
    modalClass += ' smokeandmirrors-theme';
  }

  return (
    <div className="cart-review-modal-overlay" onClick={onClose}>
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="cart-review-modal-header">
          <h2>Cart Review</h2>
          <button className="cart-review-close-btn" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="cart-review-modal-content">
          {consolidatedItems.length === 0 ? (
            <p className="cart-review-empty">Your cart is empty</p>
          ) : (
            <table className="cart-review-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Details</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {consolidatedItems.map((item, index) => (
                  <tr key={index} className={item.isCash ? 'cash-row' : 'credit-row'}>
                    <td className="item-name-cell">
                      <span className="item-name">{item.name}</span>
                      {item.expansionName && (
                        <span className="item-set">{item.expansionName}</span>
                      )}
                    </td>
                    <td className="item-details-cell">
                      <span className="item-condition">{item.displayCondition}</span>
                      <span className="item-printing">{item.displayPrinting}</span>
                      {item.language && item.language !== 'EN' && (
                        <span className="item-language">{item.language}</span>
                      )}
                      <span className={`item-payment-type ${item.isCash ? 'cash' : 'credit'}`}>
                        {item.isCash ? 'Cash' : 'Credit'}
                      </span>
                    </td>
                    <td className="item-qty-cell">
                      <span className="item-qty">x{item.quantity}</span>
                    </td>
                    <td className="item-price-cell">
                      <span className="item-unit-price">{currencySymbol}{item.unitPrice.toFixed(2)}</span>
                      {item.quantity > 1 && (
                        <span className="item-line-total">= {currencySymbol}{item.lineTotal.toFixed(2)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="cart-review-modal-summary">
          <div className="summary-row">
            <span>Cash Subtotal:</span>
            <span>{currencySymbol}{totals.cash.toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Credit Subtotal:</span>
            <span>{currencySymbol}{totals.credit.toFixed(2)}</span>
          </div>
          <div className="summary-row total-row">
            <span>Total:</span>
            <span>{currencySymbol}{totals.total.toFixed(2)}</span>
          </div>
        </div>

        <div className="cart-review-modal-footer">
          <div className="cart-review-actions">
            <button className="cart-review-print-btn" onClick={handlePrint}>
              Print
            </button>
            <button className="cart-review-save-btn" onClick={handleSave}>
              Save
            </button>
          </div>
          <button className="cart-review-close-btn-footer" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartReviewModal;
